import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { applicationTypeLabel, italianName, localizedName, translatedText } from "@/i18n/content";
import type { Application } from "@/hooks/useBioNexusData";
import { splitImageNote } from "@/lib/text";
import { cn } from "@/lib/utils";

interface ApplicationCardProps {
  application: Application;
  showDescription?: boolean;
  className?: string;
}

const ApplicationCard = ({ application, showDescription = false, className }: ApplicationCardProps) => {
  const { m, locale } = useI18n();
  const desc = translatedText(application, "description", application.description, locale);
  const { body } = splitImageNote(desc.text || "");
  const title = localizedName(locale, application.title, application.title_cn, italianName(application, "title"));
  const year = (application as Application & { year?: number | null }).year;

  return (
    <Link
      to={`/applications/${application.id}`}
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card transition-[border-color,transform,box-shadow] duration-500 ease-expo-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_24px_50px_-30px_rgba(0,0,0,0.45)]",
        className,
      )}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        {application.image_url ? (
          <img
            src={application.image_url}
            alt={application.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-expo-out group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-primary/15 to-muted" />
        )}
        <span className="absolute left-3 top-3 rounded-full bg-card/90 backdrop-blur-md px-3 py-1 text-xs font-medium capitalize text-foreground/80">
          {applicationTypeLabel(m, application.application_type)}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <p lang="en" className="text-sm text-muted-foreground line-clamp-1">
          {[application.category, year].filter(Boolean).join(" · ")}
        </p>
        <h3 className="mt-2 font-display text-[1.8rem] font-semibold leading-snug tracking-tight line-clamp-2 group-hover:text-primary transition-colors">
          <span lang={title.primaryLang}>{title.primary}</span>
        </h3>
        {title.secondary && <p lang="en" className="mt-1 text-sm text-muted-foreground line-clamp-1">{title.secondary}</p>}
        {showDescription && body && <p lang={desc.lang} className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-2">{body}</p>}
        <span className="mt-auto pt-6 text-sm font-medium text-primary">
          {m.common.readCase} <span className="arrow-nudge">→</span>
        </span>
      </div>
    </Link>
  );
};

export default ApplicationCard;
